// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import type {
  Catalog,
  CatalogingOptions,
  CatalogProperties,
  Dispatch,
  ExclusionInfo,
} from 'state/types';
import {
  getCatalogExclusionInfo,
  getCatalogingOptions,
  getCatalogProperties,
} from 'state/selectors';
import {
  getCatalogProperties as getCatalogPropertiesCommand,
  setExclusionInfo,
  updateCatalogingOptions,
} from 'state/commands/catalogCommands';
import localizedStrings from 'util/localizedStrings';
import { CATALOG_PROPERTY_KEY } from 'util/constants';
import { bindActionCreatorsForm } from 'util/safeDispatch';
import { isVaultCatalog } from 'util/catalogUtils';
import { actions as modalPromptActions } from 'portfolio-shared/state/reducers/modalPromptReducer';
import FileAndFolderExclusions from './FileAndFolderExclusions';
import FileTypesComponent from './FileTypesComponent';

const { showModalPrompt } = modalPromptActions;

const OPTION_FILENAME = 1;
const OPTION_PARENT_FOLDER = 2;
const OPTION_FOLDER_TREE = 4;
const OPTION_VOLUME = 8;
const OPTION_SERVER = 16;

type Props = {
  catalog: Catalog,
  catalogingOptions: CatalogingOptions,
  catalogProperties: CatalogProperties,
  exclusionInfo: ExclusionInfo,
  getCatalogPropertiesCommand: typeof getCatalogPropertiesCommand,
  intl: IntlShape,
  setExclusionInfo: typeof setExclusionInfo,
  showModalPrompt: typeof showModalPrompt,
  updateCatalogingOptions: typeof updateCatalogingOptions,
};

class CatalogingOptionsForm extends Component<Props> {
  onChangeKeywordPathStyle: Function;

  constructor() {
    super();
    this.onChangeKeywordPathStyle = this.onChangeKeywordPathStyle.bind(this);
  }

  componentDidMount() {
    if (this.props.catalog) {
      this.props.getCatalogPropertiesCommand(this.props.catalog.catalogId, [
        CATALOG_PROPERTY_KEY.CATALOGING_OPTIONS,
        CATALOG_PROPERTY_KEY.EXCLUSION_INFO,
        CATALOG_PROPERTY_KEY.CATALOG_DETAILS,
      ]);
    }
  }

  async onChangeKeywordPathStyle(event, flag) {
    const { catalogingOptions } = this.props;
    let keywordsFromPathStyle =
      catalogingOptions && catalogingOptions.keywordsFromPathStyle;
    const checked = event.target.checked;
    if (checked) {
      keywordsFromPathStyle += flag;
    } else {
      keywordsFromPathStyle -= flag;
    }
    const newCatOptions = { ...catalogingOptions, keywordsFromPathStyle };
    await this.props.updateCatalogingOptions(
      this.props.catalog.catalogId,
      newCatOptions
    );
  }

  isKeywordOptionSelected(catOptions: CatalogingOptions, flag: number) {
    return (catOptions && catOptions.keywordsFromPathStyle & flag) === flag;
  }

  renderKeywordFromPathCheckbox(option) {
    const {
      catalogingOptions,
      intl: { formatMessage },
    } = this.props;

    const { flag, label, isDisabled } = option;

    return (
      <div className="field" key={flag}>
        <input
          checked={this.isKeywordOptionSelected(catalogingOptions, flag)}
          className="is-checkradio"
          id={flag}
          onChange={e => this.onChangeKeywordPathStyle(e, flag)}
          disabled={isDisabled}
          type="checkbox"
          data-test-name={'checkbox-for-' + label}
        />
        <label htmlFor={flag}>{formatMessage(localizedStrings[label])}</label>
      </div>
    );
  }

  renderKeywordsFromPathComponent() {
    const { catalogProperties } = this.props;

    const checkboxOptions = [
      {
        flag: OPTION_FILENAME,
        label: 'CATALOG_OPTIONS_CREATE_KEYWORDS_OPT_FILENAME',
        isDisabled: false,
      },
      {
        flag: OPTION_PARENT_FOLDER,
        label: 'CATALOG_OPTIONS_CREATE_KEYWORDS_OPT_PARENT',
        isDisabled: false,
      },
      {
        flag: OPTION_FOLDER_TREE,
        label: 'CATALOG_OPTIONS_CREATE_KEYWORDS_OPT_TREE',
        isDisabled: false,
      },
      {
        flag: OPTION_VOLUME,
        label: 'CATALOG_OPTIONS_CREATE_KEYWORDS_OPT_VOLUME',
        isDisabled: isVaultCatalog(catalogProperties),
      },
      {
        flag: OPTION_SERVER,
        label: 'CATALOG_OPTIONS_CREATE_KEYWORDS_OPT_SERVER',
        isDisabled: isVaultCatalog(catalogProperties),
      },
    ];

    return (
      <section className="box">
        <h2 className="subtitle">
          <FormattedMessage
            {...localizedStrings.CATALOG_OPTIONS_CREATE_KEYWORDS_LABEL}
          />
        </h2>

        {checkboxOptions.map(option =>
          this.renderKeywordFromPathCheckbox(option)
        )}
      </section>
    );
  }

  render() {
    const { catalog, exclusionInfo } = this.props;
    return (
      <div
        className="page-contents-textured portfolio-form-container"
        data-test-name="catalog-options-page"
      >
        {this.renderKeywordsFromPathComponent()}

        <FileAndFolderExclusions
          catalogId={catalog.catalogId}
          exclusionInfo={exclusionInfo}
          setExclusionInfo={this.props.setExclusionInfo}
          showModalPrompt={this.props.showModalPrompt}
        />

        <FileTypesComponent exclusionInfo={exclusionInfo} catalog={catalog} />
      </div>
    );
  }
}

export default connect(
  state => ({
    catalogingOptions: getCatalogingOptions(state),
    catalogProperties: getCatalogProperties(state),
    exclusionInfo: getCatalogExclusionInfo(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsForm({
      getCatalogPropertiesCommand,
      setExclusionInfo,
      showModalPrompt,
      updateCatalogingOptions,
    })
)(injectIntl(CatalogingOptionsForm));



// WEBPACK FOOTER //
// ./src/view/Pages/CatalogsPage/CatalogingOptionsForm.js